import { useMemo } from 'react';
import { GraphData, GraphNode, GraphEdge, NodeKind } from '../types';
import { getKindConfig, STATUS_GLOW } from './constants';

export interface Graph3DNode {
  id: string;
  kind: NodeKind;
  label: string;
  status: string;
  color: string;
  glowColor: string;
  size: number;
  clusterId: string;
  props: Record<string, any>;
  // set by force-graph at runtime
  x?: number;
  y?: number;
  z?: number;
}

export interface Graph3DEdge {
  source: string;
  target: string;
  type: string;
  animated: boolean;
  color: string;
  width: number;
  opacity: number;
}

export interface Graph3DCluster {
  id: string;
  kind: NodeKind | '_default';
  label: string;
  color: string;
  nodeCount: number;
  visible: boolean;
}

export interface Graph3DData {
  nodes: Graph3DNode[];
  links: Graph3DEdge[];
  clusters: Graph3DCluster[];
}

const ANIMATED_EDGE_TYPES = new Set(['publishes', 'consumes', 'emits', 'generates_data']);

function connectionCount(nodeId: string, edges: GraphEdge[]): number {
  return edges.filter(e => e.from === nodeId || e.to === nodeId).length;
}

/**
 * Stable dedup key for a node — used when merging Standards + Infrastructure sources.
 * Strips synthetic prefixes to expose the underlying registry ID or entity _id.
 */
export function stableNodeKey(node: GraphNode): string {
  const id = node.id;
  // Standards plane: "iku:products" -> "products", "template:content:youtube_reel" -> "content:youtube_reel"
  if (id.startsWith('iku:')) return `iku:${id.slice(4)}`;
  if (id.startsWith('template:')) return `template:${id.slice(9)}`;
  // GraphDiscovery synthetic: "template-507f1f..." -> use props.entity_id or props._id
  const entityId = node.props?.entity_id || node.props?._id;
  if (entityId) return String(entityId);
  return id;
}

/**
 * Merge two GraphData sources (Standards takes priority on collision by stableNodeKey).
 * Extra edges from Infrastructure that reference existing Standard node IDs are remapped.
 */
export function mergeGraphSources(standards: GraphData, infrastructure: GraphData): GraphData {
  const keyToStandardId = new Map<string, string>();
  const mergedNodes: GraphNode[] = [];

  // Index all standard nodes by their stable key
  for (const node of standards.nodes) {
    const key = stableNodeKey(node);
    keyToStandardId.set(key, node.id);
    mergedNodes.push(node);
  }

  // Add infrastructure nodes that don't collide
  const infraIdRemap = new Map<string, string>(); // infra id -> canonical id
  for (const node of infrastructure.nodes) {
    const key = stableNodeKey(node);
    if (keyToStandardId.has(key)) {
      // Collision: remap infra id to standard id
      infraIdRemap.set(node.id, keyToStandardId.get(key)!);
    } else {
      mergedNodes.push(node);
      infraIdRemap.set(node.id, node.id);
    }
  }

  const allNodeIds = new Set(mergedNodes.map(n => n.id));

  // Merge edges, remapping infra node IDs to canonical IDs
  const seenEdgeKeys = new Set<string>();
  const mergedEdges: GraphEdge[] = [];

  const addEdge = (edge: GraphEdge) => {
    const key = `${edge.from}|${edge.to}|${edge.type}`;
    if (!seenEdgeKeys.has(key) && allNodeIds.has(edge.from) && allNodeIds.has(edge.to)) {
      seenEdgeKeys.add(key);
      mergedEdges.push(edge);
    }
  };

  for (const edge of standards.edges) addEdge(edge);
  for (const edge of infrastructure.edges) {
    const from = infraIdRemap.get(edge.from) || edge.from;
    const to = infraIdRemap.get(edge.to) || edge.to;
    addEdge({ ...edge, from, to, id: `infra:${edge.id}` });
  }

  return { nodes: mergedNodes, edges: mergedEdges };
}

export function transformToGraph3D(data: GraphData): Graph3DData {
  const { nodes, edges } = data;

  // Build cluster map
  const clusterMap = new Map<string, Graph3DCluster>();

  const graph3DNodes: Graph3DNode[] = nodes.map(node => {
    const cfg = getKindConfig(node.kind);
    const glowColor = STATUS_GLOW[node.status] || cfg.glowColor;
    const connCount = connectionCount(node.id, edges);
    const size = Math.max(4, Math.log(connCount + 1) * 4);
    const clusterId = node.kind;

    if (!clusterMap.has(clusterId)) {
      clusterMap.set(clusterId, {
        id: clusterId,
        kind: node.kind,
        label: cfg.clusterLabel,
        color: cfg.color,
        nodeCount: 0,
        visible: true,
      });
    }
    clusterMap.get(clusterId)!.nodeCount++;

    return {
      id: node.id,
      kind: node.kind,
      label: node.label,
      status: node.status,
      color: cfg.color,
      glowColor,
      size,
      clusterId,
      props: node.props,
    };
  });

  const graph3DLinks: Graph3DEdge[] = edges.map(edge => {
    const animated = ANIMATED_EDGE_TYPES.has(edge.type);
    return {
      source: edge.from,
      target: edge.to,
      type: edge.type,
      animated,
      color: animated ? '#F59E0B' : '#475569',
      width: animated ? 2 : 1,
      opacity: animated ? 0.9 : 0.4,
    };
  });

  return {
    nodes: graph3DNodes,
    links: graph3DLinks,
    clusters: Array.from(clusterMap.values()).sort((a, b) => b.nodeCount - a.nodeCount),
  };
}

export function useGraph3DData(data: GraphData | undefined): Graph3DData {
  return useMemo(() => {
    if (!data) return { nodes: [], links: [], clusters: [] };
    return transformToGraph3D(data);
  }, [data]);
}
